import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Stepper, Step, StepLabel,
  Snackbar, Card, CardContent, CardActions, Chip, Grid, MenuItem,
  Accordion, AccordionSummary, AccordionDetails, Checkbox, FormControlLabel, 
  Tabs, Tab, // <-- Add Tab components
  useTheme, useMediaQuery
} from '@mui/material';
import { Add, Edit, Delete, AddCircle, RemoveCircle, ExpandMore } from '@mui/icons-material';
import { buildingsAPI, facilitiesAPI } from '../services/api';

const safeArray = (val) => Array.isArray(val) ? val : [];

// TabPanel component for payment gateway tabs
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const buildingDialogPaperSx = {
  borderRadius: { xs: 0, sm: 2 },
  maxHeight: { xs: '100vh', sm: 'calc(100vh - 64px)' },
};

const buildingDialogTitleSx = {
  px: { xs: 2.5, sm: 3 },
  py: { xs: 2, sm: 2.5 },
  borderBottom: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const buildingDialogContentSx = {
  bgcolor: '#f8fafc',
  px: { xs: 2, sm: 3 },
  py: { xs: 2, sm: 3 },
};

const buildingDialogActionsSx = {
  px: { xs: 2, sm: 3 },
  py: 2,
  borderTop: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  gap: 1,
};

const BuildingFormSection = ({ title, description, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 2, sm: 2.5 },
      borderRadius: 2,
      bgcolor: 'background.paper',
      borderColor: '#e5e7eb',
    }}
  >
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
    {children}
  </Paper>
);

const facilityDefaults = {
  POOL: {
    timings: ['06:00-10:00', '16:00-19:00'],
    status: 'OPEN',
    bookingRequired: true,
    numberOfSlots: 1, // One pool area
  },
  GYM: {
    timings: ['05:00-22:00'],
    status: 'OPEN',
    bookingRequired: false,
    numberOfSlots: 50, // Multiple equipment/people capacity
  },
  CLUB_HOUSE: {
    timings: ['10:00-20:00'],
    status: 'OPEN',
    bookingRequired: true,
    numberOfSlots: 1, // One hall
  },
  COMMUNITY_HALL: {
    timings: ['09:00-21:00'],
    status: 'OPEN',
    bookingRequired: true,
    numberOfSlots: 1, // One hall
  },
  SHUTTLE_COURT: {
    timings: ['06:00-10:00', '16:00-21:00'],
    status: 'OPEN',
    bookingRequired: true,
    numberOfSlots: 2, // Typically 2 courts
  },
  SQUASH_COURT: {
    timings: ['06:00-10:00', '16:00-21:00'],
    status: 'OPEN',
    bookingRequired: true,
    numberOfSlots: 2, // Typically 2 courts
  },
  TENNIS_COURT: {
    timings: ['06:00-10:00', '16:00-21:00'],
    status: 'OPEN',
    bookingRequired: true,
    numberOfSlots: 2, // Typically 2 courts
  },
  CRICKET_NET: {
    timings: ['06:00-10:00', '16:00-21:00'],
    status: 'OPEN',
    bookingRequired: true,
    numberOfSlots: 4, // Typically 4 nets
  },
  BASKET_BALL_COURT: {
    timings: ['06:00-10:00', '16:00-21:00'],
    status: 'OPEN',
    bookingRequired: true,
    numberOfSlots: 1, // One court
  },
  CHESS: {
    timings: ['09:00-21:00'],
    status: 'OPEN',
    bookingRequired: false,
    numberOfSlots: 6, // Multiple chess boards
  },
  CARROMS: {
    timings: ['09:00-21:00'],
    status: 'OPEN',
    bookingRequired: false,
    numberOfSlots: 4, // Multiple carrom boards
  },
  TABLE_TENNIS: {
    timings: ['09:00-21:00'],
    status: 'OPEN',
    bookingRequired: false,
    numberOfSlots: 3, // Multiple tables
  },
  BADMINTON: {
    timings: ['06:00-10:00', '16:00-21:00'],
    status: 'OPEN',
    bookingRequired: true,
    numberOfSlots: 4, // Typically 4 courts
  },
  FOOSBALL: {
    timings: ['09:00-21:00'],
    status: 'OPEN',
    bookingRequired: false,
    numberOfSlots: 2, // Multiple tables
  },
  SNOOKER: {
    timings: ['09:00-21:00'],
    status: 'OPEN',
    bookingRequired: false,
    numberOfSlots: 2, // Multiple tables
  },
  DARTS: {
    timings: ['09:00-21:00'],
    status: 'OPEN',
    bookingRequired: false,
    numberOfSlots: 3, // Multiple dart boards
  },
  BOARD_GAMES: {
    timings: ['09:00-21:00'],
    status: 'OPEN',
    bookingRequired: false,
    numberOfSlots: 8, // Multiple tables/sets
  },
  OTHER: {
    timings: ['09:00-18:00'],
    status: 'OPEN',
    bookingRequired: false,
    numberOfSlots: 1, // Default
  }
};

const initialFacility = {
  facilityId: '',
  name: '',
  type: '',
  status: 'OPEN',
  timings: [],
  bookingRequired: false,
  numberOfSlots: 1,
  chargeAmount: '',
  chargeUnit: '',
  description: ''
};

const facilityTypes = [
  { value: 'POOL', label: 'Swimming Pool' },
  { value: 'GYM', label: 'Gym' },
  { value: 'CLUB_HOUSE', label: 'Club House' },
  { value: 'COMMUNITY_HALL', label: 'Community Hall' },
  { value: 'SHUTTLE_COURT', label: 'Shuttle Court' },
  { value: 'SQUASH_COURT', label: 'Squash Court' },
  { value: 'TENNIS_COURT', label: 'Tennis Court' },
  { value: 'CRICKET_NET', label: 'Cricket Practice Net' },
  { value: 'BASKET_BALL_COURT', label: 'Basket Ball Court' },
  { value: 'CHESS', label: 'Chess' },
  { value: 'CARROMS', label: 'Carroms' },
  { value: 'TABLE_TENNIS', label: 'Table Tennis' },         // Added
  { value: 'BADMINTON', label: 'Badminton' },               // Added
  { value: 'FOOSBALL', label: 'Foosball' },                 // Added
  { value: 'SNOOKER', label: 'Snooker / Billiards' },       // Added
  { value: 'DARTS', label: 'Darts' },                       // Added
  { value: 'BOARD_GAMES', label: 'Board Games' },           // Added
  { value: 'OTHER', label: 'Other' }
];

const chargeUnits = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'per_booking', label: 'Per Booking' }
];

const Buildings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [expandedBuildingId, setExpandedBuildingId] = useState(null);
  const [facilitiesMap, setFacilitiesMap] = useState({});
  const steps = ['Basic Info', 'Blocks', 'Facilities', 'Review'];
  const editSteps = steps;
  const [formData, setFormData] = useState({
    buildingName: '',
    billingMode: 'MAINTENANCE',
    address: '',
    totalFloors: '',
    totalUnits: '',
    blocks: [],
    facilities: [],
    razorpayKeyId: '',
    razorpayKeySecret: '',
    ccavenueAccessCode: '',
    ccavenueMerchantId: '',
    ccavenueWorkingKey: '',
    ccavenueSubAccountId: ''
  });
  const [error, setError] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [editActiveStep, setEditActiveStep] = useState(0);
  const [editRazorpayCredentials, setEditRazorpayCredentials] = useState(false);
  const [editCCavenueCredentials, setEditCCavenueCredentials] = useState(false);
  const [paymentTab, setPaymentTab] = useState(0);
  const [editPaymentTab, setEditPaymentTab] = useState(0);
  const queryClient = useQueryClient();
  const { data: buildings = [], isLoading } = useQuery('buildings', async () => {
    const response = await buildingsAPI.getAll();
    console.log('[Buildings] Fetched buildings data:', response.data);
    return response.data;
  });

  // Update createMutation to also create facilities after building creation
  const createMutation = useMutation(buildingsAPI.create, {
    onSuccess: async (buildingRes) => {
      console.log('Building created successfully:', buildingRes.data);
      
      // If facilities are present, create them for the building
      if (formData.facilities && formData.facilities.length > 0) {
        console.log('Creating facilities for building:', formData.facilities);
        for (const facility of formData.facilities) {
          try {
            await facilitiesAPI.create({
              ...facility,
              buildingId: buildingRes.data.buildingId,
            });
            console.log('Facility created:', facility.name);
          } catch (facilityError) {
            console.error('Failed to create facility:', facility.name, facilityError);
          }
        }
      }
      
      queryClient.invalidateQueries('buildings');
      handleClose();
      console.log('Building creation process completed');
    },
    onError: (error) => {
      console.error('Building creation failed:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create building';
      setError(errorMessage);
    },
  });

  // Update updateMutation to also update facilities after building update
  const updateMutation = useMutation(
    async ({ buildingId, data }) => {
      const res = await buildingsAPI.update(buildingId, data);
      // Update or create facilities as needed
      if (data.facilities && data.facilities.length > 0) {
        for (const facility of data.facilities) {
          if (facility.facilityId) {
            await facilitiesAPI.update({
              ...facility,
              buildingId,
            });
          } else {
            await facilitiesAPI.create({
              ...facility,
              buildingId,
            });
          }
        }
      }
      return res;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('buildings');
        setEditDialogOpen(false);
        setEditRazorpayCredentials(false); // Reset edit checkbox
      },
      onError: (error) => {
        setError(error.response?.data?.message || 'Failed to update building');
      },
    }
  );

  const deleteMutation = useMutation(
    (buildingId) => buildingsAPI.delete(buildingId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('buildings');
      },
      onError: (error) => {
        setDeleteError(error.response?.data?.message || 'Failed to delete building');
      },
    }
  );

  const handleOpen = () => {
    setFormData({ 
      buildingName: '', 
      billingMode: 'MAINTENANCE',
      address: '', 
      totalFloors: '', 
      totalUnits: '', 
      blocks: [],
      facilities: [],
      razorpayKeyId: '',
      razorpayKeySecret: '',
      ccavenueAccessCode: '',
      ccavenueMerchantId: '',
      ccavenueWorkingKey: '',
      ccavenueSubAccountId: ''
    });
    setError('');
    setPaymentTab(0);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({ 
      buildingName: '', 
      billingMode: 'MAINTENANCE',
      address: '', 
      totalFloors: '', 
      totalUnits: '', 
      blocks: [],
      facilities: [],
      razorpayKeyId: '',
      razorpayKeySecret: '',
      ccavenueAccessCode: '',
      ccavenueMerchantId: '',
      ccavenueWorkingKey: '',
      ccavenueSubAccountId: ''
    });
    setActiveStep(0);
    setPaymentTab(0);
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log('Form submission started');
    console.log('Form data:', formData);
    
    // Validate form data
    if (!formData.buildingName || !formData.address || !formData.totalFloors || !formData.totalUnits) {
      const missingFields = [];
      if (!formData.buildingName) missingFields.push('Building Name');
      if (!formData.address) missingFields.push('Address');
      if (!formData.totalFloors) missingFields.push('Total Floors');
      if (!formData.totalUnits) missingFields.push('Total Units');
      
      const errorMsg = `Please fill in all required fields: ${missingFields.join(', ')}`;
      console.error('Validation error:', errorMsg);
      setError(errorMsg);
      return;
    }

    // Validate blocks (optional - but if blocks are added, they must have required fields)
    if (formData.blocks && formData.blocks.length > 0) {
      const invalidBlocks = formData.blocks.filter(block => !block.blockName || !block.numFloors);
      if (invalidBlocks.length > 0) {
        const errorMsg = 'Please fill in all block details (Name and Number of Floors)';
        console.error('Block validation error:', errorMsg);
        setError(errorMsg);
        return;
      }
    }

    console.log('Validation passed, calling create mutation');
    // Call the create mutation
    createMutation.mutate(formData);
  };

  const handleEditOpen = async (building) => {
    console.log('[Edit] Opening edit dialog for building:', building);
    setError('');
    setEditActiveStep(0); // Reset to first step when opening edit dialog
    setEditRazorpayCredentials(false); // Reset Razorpay edit checkbox
    setEditCCavenueCredentials(false); // Reset CCAvenue edit checkbox
    setEditPaymentTab(0); // Reset payment tab
    // Fetch latest facilities for this building before opening the dialog
    let facilities = [];
    try {
      const res = await facilitiesAPI.getAll(building.buildingId);
      facilities = res.data || [];
      console.log('[Edit] Fetched facilities:', facilities);
    } catch (error) {
      console.log('[Edit] Error fetching facilities:', error);
      facilities = [];
    }
    const formData = { ...building, billingMode: building.billingMode === 'RENTAL' ? 'RENTAL' : 'MAINTENANCE', facilities };
    console.log('[Edit] Setting editFormData to:', formData);
    setEditFormData(formData);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditFormData(null);
    setError('');
    setEditActiveStep(0); // Reset step when closing
    setEditRazorpayCredentials(false); // Reset Razorpay edit checkbox
    setEditCCavenueCredentials(false); // Reset CCAvenue edit checkbox
    setEditPaymentTab(0); // Reset payment tab
  };

  // Fix the handleEditSubmit to only submit on final step
  const handleEditSubmit = (e) => {
    e.preventDefault();
    // Only submit if we're on the final step
    if (editActiveStep === editSteps.length - 1) {
      if (editFormData && editFormData.buildingId) {
        updateMutation.mutate({ buildingId: editFormData.buildingId, data: editFormData });
      }
    }
  };

  // Add step validation function for edit dialog
  const canProceedToNextEditStep = () => {
    if (editActiveStep === 0) {
      const canProceed = editFormData?.buildingName && editFormData?.address && 
             editFormData?.totalFloors && editFormData?.totalUnits;
      console.log('[Edit] Can proceed to next step:', canProceed, {
        buildingName: editFormData?.buildingName,
        address: editFormData?.address,
        totalFloors: editFormData?.totalFloors,
        totalUnits: editFormData?.totalUnits
      });
      return canProceed;
    }
    return true; // Other steps don't have required validation for now
  };

  // --- Block Handlers for Add Dialog ---
  const handleAddBlock = () => {
    // Block ID is a running sequence starting from 1 per building
    const nextBlockId = ((formData.blocks || []).length + 1).toString();
    setFormData(prev => ({
      ...prev,
      blocks: [...(prev.blocks || []), { blockId: nextBlockId, blockName: '', numFloors: '' }]
    }));
  };

  const handleRemoveBlock = (idx) => {
    setFormData(prev => ({
      ...prev,
      blocks: prev.blocks.filter((_, i) => i !== idx)
    }));
  };
  const handleBlockChange = (idx, field, value) => {
    if (field === 'blockName') {
      const exists = formData.blocks.some(
        (block, i) => i !== idx && block.blockName.trim().toLowerCase() === value.trim().toLowerCase()
      );
      if (exists) {
        setError('Block Name must be unique within the building.');
        return;
      } else {
        setError('');
      }
    }
    setFormData(prev => ({
      ...prev,
      blocks: prev.blocks.map((block, i) =>
        i === idx ? { ...block, [field]: value } : block
      )
    }));
  };

  // --- Block Handlers for Edit Dialog ---
  const handleEditAddBlock = () => {
    const nextBlockId = ((editFormData.blocks || []).length + 1).toString();
    setEditFormData(prev => ({
      ...prev,
      blocks: [...(prev.blocks || []), { blockId: nextBlockId, blockName: '', numFloors: '' }]
    }));
  };
  const handleEditRemoveBlock = (idx) => {
    setEditFormData(prev => ({
      ...prev,
      blocks: prev.blocks.filter((_, i) => i !== idx)
    }));
  };
  const handleEditBlockChange = (idx, field, value) => {
    if (field === 'blockName') {
      const exists = (editFormData.blocks || []).some(
        (block, i) => i !== idx && block.blockName.trim().toLowerCase() === value.trim().toLowerCase()
      );
      if (exists) {
        setError('Block Name must be unique within the building.');
        return;
      } else {
        setError('');
      }
    }
    setEditFormData(prev => ({
      ...prev,
      blocks: prev.blocks.map((block, i) =>
        i === idx ? { ...block, [field]: value } : block
      )
    }));
  };

  // --- Facility Handlers for Add Dialog ---
  const handleAddFacility = () => {
    setFormData(prev => ({
      ...prev,
      facilities: [...(prev.facilities || []), { ...initialFacility }]
    }));
  };
  const handleRemoveFacility = (idx) => {
    setFormData(prev => ({
      ...prev,
      facilities: (prev.facilities || []).filter((_, i) => i !== idx)
    }));
  };
  const handleFacilityChange = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      facilities: (prev.facilities || []).map((facility, i) =>
        i === idx ? { ...facility, [field]: value } : facility
      )
    }));
  };

  // --- Facility Handlers for Edit Dialog ---
  const handleEditAddFacility = () => {
    setEditFormData(prev => ({
      ...prev,
      facilities: [...(prev.facilities || []), { ...initialFacility }]
    }));
  };
  const handleEditRemoveFacility = (idx) => {
    setEditFormData(prev => ({
      ...prev,
      facilities: (prev.facilities || []).filter((_, i) => i !== idx)
    }));
  };
  const handleEditFacilityChange = (idx, field, value) => {
    setEditFormData(prev => ({
      ...prev,
      facilities: (prev.facilities || []).map((facility, i) =>
        i === idx ? { ...facility, [field]: value } : facility
      )
    }));
  };

  // Facility Type Change Handler
  const handleFacilityTypeChange = (idx, value) => {
    const defaults = {
      ...facilityDefaults[value],
      chargeAmount: facilityDefaults[value]?.chargeAmount ?? '',
      chargeUnit: facilityDefaults[value]?.chargeUnit ?? '',
    };
    setFormData(prev => ({
      ...prev,
      facilities: (prev.facilities || []).map((facility, i) =>
        i === idx
          ? {
              ...facility,
              type: value,
              timings: defaults.timings ? [...defaults.timings] : [],
              status: defaults.status || 'OPEN',
              bookingRequired: typeof defaults.bookingRequired === 'boolean' ? defaults.bookingRequired : false,
              numberOfSlots: defaults.numberOfSlots || 1,
              chargeAmount: defaults.chargeAmount !== undefined ? defaults.chargeAmount : '',
              chargeUnit: defaults.chargeUnit || ''
            }
          : facility
      )
    }));
  };

  // Facility Timings Handlers
  const handleFacilityTimingsChange = (idx, slotIdx, value) => {
    setFormData(prev => ({
      ...prev,
      facilities: (prev.facilities || []).map((facility, i) =>
        i === idx
          ? {
              ...facility,
              timings: facility.timings.map((slot, sIdx) =>
                sIdx === slotIdx ? value : slot
              )
            }
          : facility
      )
    }));
  };
  const handleAddFacilityTiming = (idx) => {
    setFormData(prev => ({
      ...prev,
      facilities: (prev.facilities || []).map((facility, i) =>
        i === idx
          ? { ...facility, timings: [...(facility.timings || []), ''] }
          : facility
      )
    }));
  };
  const handleRemoveFacilityTiming = (idx, slotIdx) => {
    setFormData(prev => ({
      ...prev,
      facilities: (prev.facilities || []).map((facility, i) =>
        i === idx
          ? { ...facility, timings: facility.timings.filter((_, sIdx) => sIdx !== slotIdx) }
          : facility
      )
    }));
  };

  // Edit versions of facility handlers
  const handleEditFacilityTypeChange = (idx, value) => {
    const defaults = {
      ...facilityDefaults[value],
      chargeAmount: facilityDefaults[value]?.chargeAmount ?? '',
      chargeUnit: facilityDefaults[value]?.chargeUnit ?? '',
    };
    setEditFormData(prev => ({
      ...prev,
      facilities: (prev.facilities || []).map((facility, i) =>
        i === idx
          ? {
              ...facility,
              type: value,
              timings: defaults.timings ? [...defaults.timings] : [],
              status: defaults.status || 'OPEN',
              bookingRequired: typeof defaults.bookingRequired === 'boolean' ? defaults.bookingRequired : false,
              numberOfSlots: defaults.numberOfSlots || 1,
              chargeAmount: defaults.chargeAmount !== undefined ? defaults.chargeAmount : '',
              chargeUnit: defaults.chargeUnit || ''
            }
          : facility
      )
    }));
  };

  const handleEditFacilityTimingsChange = (idx, slotIdx, value) => {
    setEditFormData(prev => ({
      ...prev,
      facilities: (prev.facilities || []).map((facility, i) =>
        i === idx
          ? { ...facility, timings: facility.timings.map((slot, sIdx) => sIdx === slotIdx ? value : slot) }
          : facility
      )
    }));
  };

  const handleEditAddFacilityTiming = (idx) => {
    setEditFormData(prev => ({
      ...prev,
      facilities: (prev.facilities || []).map((facility, i) =>
        i === idx
          ? { ...facility, timings: [...(facility.timings || []), ''] }
          : facility
      )
    }));
  };

  const handleEditRemoveFacilityTiming = (idx, slotIdx) => {
    setEditFormData(prev => ({
      ...prev,
      facilities: (prev.facilities || []).map((facility, i) =>
        i === idx
          ? { ...facility, timings: facility.timings.filter((_, sIdx) => sIdx !== slotIdx) }
          : facility
      )
    }));
  };

  // Delete building handler
  const handleDeleteBuilding = async (buildingId) => {
    if (window.confirm('Are you sure you want to delete this building? This action cannot be undone.')) {
      try {
        await buildingsAPI.delete(buildingId);
        // Refetch buildings to update the list
        queryClient.invalidateQueries('buildings');
      } catch (error) {
        console.error('Failed to delete building:', error);
        alert('Failed to delete building. Please try again.');
      }
    }
  };

  // Fetch facilities for a building only when its accordion is expanded
  const handleAccordionChange = (buildingId) => async (event, isExpanded) => {
    setExpandedBuildingId(isExpanded ? buildingId : null);
    if (isExpanded && !facilitiesMap[buildingId]) {
      try {
        const res = await facilitiesAPI.getAll(buildingId);
        setFacilitiesMap(prev => ({ ...prev, [buildingId]: res.data }));
      } catch (err) {
        setFacilitiesMap(prev => ({ ...prev, [buildingId]: [] }));
      }
    }
  };

  if (buildings && buildings.message) {
    return <Typography color="error">{buildings.message}</Typography>;
  }
  const buildingsSafe = safeArray(buildings);

  return (
    <Box className="mobile-content-spacing" sx={{ maxWidth: 1280, mx: 'auto' }}>
      <Paper
        variant="outlined"
        sx={{
          mb: 3,
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          bgcolor: 'background.paper',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700 }}>Buildings Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage buildings, blocks, payment gateway configuration, and shared facilities.
          </Typography>
        </Box>
        
        <Button 
          variant="contained" 
          startIcon={<Add />} 
          onClick={handleOpen}
          sx={{ minHeight: 44, fontSize: isMobile ? '14px' : '16px', alignSelf: { xs: 'stretch', sm: 'center' } }}
        >
          Add Building
        </Button>
      </Paper>
      {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : buildingsSafe.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h6">No buildings found.</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>Create your first building to start configuring flats and users.</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpen}>Add Building</Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gap: 2 }}>
          {buildingsSafe.map((building) => (
            <Accordion
              key={building.buildingId}
              disableGutters
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
                '&:before': { display: 'none' },
              }}
              expanded={expandedBuildingId === building.buildingId}
              onChange={handleAccordionChange(building.buildingId)}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  '& .MuiAccordionSummary-content': {
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                  },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{building.buildingName}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{building.address}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip size="small" label={`${building.totalFloors || 0} floors`} />
                    <Chip size="small" color="primary" variant="outlined" label={`${building.totalUnits || 0} units`} />
                    <Chip size="small" label={`${(building.blocks || []).length} blocks`} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'row' }, gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Edit />}
                    sx={{ 
                      minHeight: isMobile ? 44 : 'auto',
                      fontSize: isMobile ? '14px' : '16px',
                      fontWeight: 700
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      handleEditOpen(building);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<Delete />}
                    sx={{ 
                      minHeight: isMobile ? 44 : 'auto',
                      fontSize: isMobile ? '14px' : '16px',
                      fontWeight: 700
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      handleDeleteBuilding(building.buildingId);
                    }}
                  >
                    Delete
                  </Button>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 2, bgcolor: '#f8fafc' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>Blocks</Typography>
                {(building.blocks || []).length === 0 ? (
                  <Typography color="text.secondary" sx={{ mb: 2 }}>No blocks defined.</Typography>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {(building.blocks || []).map((block, idx) => (
                      <Chip key={idx} label={`${block.blockName} - ${block.numFloors || 0} floors`} variant="outlined" />
                    ))}
                  </Box>
                )}
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>Facilities</Typography>
                {expandedBuildingId === building.buildingId ? (
                  facilitiesMap[building.buildingId] ? (
                    facilitiesMap[building.buildingId].length === 0 ? (
                      <Typography color="text.secondary">No facilities defined.</Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Timings</TableCell>
                            <TableCell>Slots</TableCell>
                            <TableCell>Charge</TableCell>
                            <TableCell>Booking Required</TableCell>
                            <TableCell>Description</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {facilitiesMap[building.buildingId].map((facility, fidx) => (
                            <TableRow key={fidx}>
                              <TableCell>{facility.name}</TableCell>
                              <TableCell>
                                {facilityTypes.find(f => f.value === facility.type)?.label || facility.type}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={facility.status}
                                  color={facility.status === 'OPEN' ? 'success' : facility.status === 'MAINTENANCE' ? 'warning' : 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{facility.timings && facility.timings.length > 0 ? facility.timings.join(', ') : '-'}</TableCell>
                              <TableCell>{facility.numberOfSlots || 1}</TableCell>
                              <TableCell>{facility.chargeAmount} {facility.chargeUnit}</TableCell>
                              <TableCell>{facility.bookingRequired ? 'Yes' : 'No'}</TableCell>
                              <TableCell>{facility.description}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )
                  ) : (
                    <Typography color="text.secondary">Loading facilities...</Typography>
                  )
                ) : null}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
      {/* Add Building Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        className="building-dialog"
        PaperProps={{ sx: buildingDialogPaperSx }}
      >
        <DialogTitle sx={buildingDialogTitleSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Add color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Add New Building</Typography>
              <Typography variant="body2" color="text.secondary">
                Configure the building, blocks, facilities, and optional payment gateway details.
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={buildingDialogContentSx}>
            <Stepper 
              activeStep={activeStep} 
              sx={{ 
                mb: 3,
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '& .MuiStepLabel-label': {
                  fontSize: isMobile ? '12px' : '14px'
                }
              }}
              orientation={isMobile ? "vertical" : "horizontal"}
            >
              {steps.map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {activeStep === 0 && (
              <Box sx={{ display: 'grid', gap: 2 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <BuildingFormSection title="Basic information" description="Name, address, and overall building capacity.">
                  <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                    <TextField label="Building Name" value={formData.buildingName} onChange={e => setFormData({ ...formData, buildingName: e.target.value })} required fullWidth />
                    <TextField label="Address" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} required fullWidth />
                    <TextField select label="Billing Mode" value={formData.billingMode} onChange={e => setFormData({ ...formData, billingMode: e.target.value })} required fullWidth>
                      <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                      <MenuItem value="RENTAL">Rental</MenuItem>
                    </TextField>
                    <TextField label="Total Floors" type="number" value={formData.totalFloors} onChange={e => setFormData({ ...formData, totalFloors: e.target.value })} required fullWidth />
                    <TextField label="Total Units" type="number" value={formData.totalUnits} onChange={e => setFormData({ ...formData, totalUnits: e.target.value })} required fullWidth />
                  </Box>
                </BuildingFormSection>

                <BuildingFormSection title="Payment gateway configuration" description="Optional credentials for online payment collection.">
                  <Tabs value={paymentTab} onChange={(e, newValue) => setPaymentTab(newValue)} aria-label="payment gateway tabs" sx={{ mb: 2 }}>
                    <Tab label="Razorpay" {...a11yProps(0)} />
                    <Tab label="CCAvenue" {...a11yProps(1)} />
                  </Tabs>
                  
                  <TabPanel value={paymentTab} index={0}>
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                      <TextField 
                        label="Razorpay Key ID" 
                        value={formData.razorpayKeyId} 
                        onChange={e => setFormData({ ...formData, razorpayKeyId: e.target.value })} 
                        placeholder="rzp_test_xxxxxxxxxx or rzp_live_xxxxxxxxxx"
                        helperText="Your Razorpay Key ID for payment processing"
                        fullWidth 
                      />
                      <TextField 
                        label="Razorpay Key Secret" 
                        type="password"
                        value={formData.razorpayKeySecret} 
                        onChange={e => setFormData({ ...formData, razorpayKeySecret: e.target.value })} 
                        placeholder="Enter your Razorpay Key Secret"
                        helperText="Will be encrypted and stored securely"
                        fullWidth 
                      />
                    </Box>
                  </TabPanel>
                  
                  <TabPanel value={paymentTab} index={1}>
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                      <TextField label="CCAvenue Merchant ID" value={formData.ccavenueMerchantId} onChange={e => setFormData({ ...formData, ccavenueMerchantId: e.target.value })} fullWidth />
                      <TextField label="CCAvenue Access Code" value={formData.ccavenueAccessCode} onChange={e => setFormData({ ...formData, ccavenueAccessCode: e.target.value })} fullWidth />
                      <TextField label="CCAvenue Working Key" type="password" value={formData.ccavenueWorkingKey} onChange={e => setFormData({ ...formData, ccavenueWorkingKey: e.target.value })} helperText="Will be encrypted and stored securely" fullWidth />
                      <TextField label="CCAvenue Sub Account ID" value={formData.ccavenueSubAccountId} onChange={e => setFormData({ ...formData, ccavenueSubAccountId: e.target.value })} helperText="Optional" fullWidth />
                    </Box>
                  </TabPanel>
                </BuildingFormSection>
              </Box>
            )}
            {activeStep === 1 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>Blocks / Towers</Typography>
                {(formData.blocks || []).map((block, idx) => (
                  <Card key={block.blockId} sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Block ID"
                            value={block.blockId}
                            InputProps={{ readOnly: true }}
                            size="small"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Block Name"
                            value={block.blockName}
                            onChange={e => handleBlockChange(idx, 'blockName', e.target.value)}
                            size="small"
                            fullWidth
                            required
                            error={!!error && error.includes('unique')}
                            helperText={!!error && error.includes('unique') ? error : ''}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Floors"
                            type="number"
                            value={block.numFloors}
                            onChange={e => handleBlockChange(idx, 'numFloors', e.target.value)}
                            size="small"
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardActions>
                      <Button size="small" color="error" onClick={() => handleRemoveBlock(idx)}>
                        Remove Block
                      </Button>
                    </CardActions>
                  </Card>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<AddCircle />}
                  onClick={handleAddBlock}
                  sx={{ mt: 1 }}
                >
                  Add Block
                </Button>
              </Box>
            )}
            {activeStep === 2 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>Facilities</Typography>
                {(formData.facilities || []).map((facility, idx) => (
                  <Card key={idx} sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            select
                            label="Type"
                            value={facility.type}
                            onChange={e => handleFacilityTypeChange(idx, e.target.value)}
                            size="small"
                            fullWidth
                          >
                            {facilityTypes.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Name"
                            value={facility.name}
                            onChange={e => handleFacilityChange(idx, 'name', e.target.value)}
                            size="small"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" sx={{ mb: 1 }}>Timings</Typography>
                          {(facility.timings || []).map((slot, slotIdx) => (
                            <Box key={slotIdx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <TextField
                                value={slot}
                                onChange={e => handleFacilityTimingsChange(idx, slotIdx, e.target.value)}
                                size="small"
                                placeholder="e.g. 06:00-10:00"
                                sx={{ mr: 1, flex: 1 }}
                              />
                              <IconButton onClick={() => handleRemoveFacilityTiming(idx, slotIdx)} size="small">
                                <RemoveCircle />
                              </IconButton>
                            </Box>
                          ))}
                          <Button
                            size="small"
                            startIcon={<AddCircle />}
                            onClick={() => handleAddFacilityTiming(idx)}
                          >
                            Add Slot
                          </Button>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            select
                            label="Status"
                            value={facility.status}
                            onChange={e => handleFacilityChange(idx, 'status', e.target.value)}
                            size="small"
                            fullWidth
                          >
                            <MenuItem value="OPEN">Open</MenuItem>
                            <MenuItem value="CLOSED">Closed</MenuItem>
                            <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Charge"
                            type="number"
                            value={facility.chargeAmount}
                            onChange={e => handleFacilityChange(idx, 'chargeAmount', e.target.value)}
                            size="small"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            select
                            label="Charge Unit"
                            value={facility.chargeUnit}
                            onChange={e => handleFacilityChange(idx, 'chargeUnit', e.target.value)}
                            size="small"
                            fullWidth
                          >
                            {chargeUnits.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Description"
                            value={facility.description}
                            onChange={e => handleFacilityChange(idx, 'description', e.target.value)}
                            size="small"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            select
                            label="Booking Required"
                            value={facility.bookingRequired ? 'true' : 'false'}
                            onChange={e => handleFacilityChange(idx, 'bookingRequired', e.target.value === 'true')}
                            size="small"
                            fullWidth
                          >
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Number of Slots"
                            type="number"
                            value={facility.numberOfSlots || 1}
                            onChange={e => {
                              const value = parseInt(e.target.value);
                              if (value >= 1 && value <= 100) {
                                handleFacilityChange(idx, 'numberOfSlots', value);
                              } else if (e.target.value === '') {
                                handleFacilityChange(idx, 'numberOfSlots', 1);
                              }
                            }}
                            size="small"
                            fullWidth
                            inputProps={{ min: 1, max: 100, step: 1 }}
                            helperText="Enter number of available slots/units (1-100)"
                            placeholder="Enter number of slots"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardActions>
                      <Button size="small" color="error" onClick={() => handleRemoveFacility(idx)}>
                        Remove Facility
                      </Button>
                    </CardActions>
                  </Card>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<AddCircle />}
                  onClick={handleAddFacility}
                  sx={{ mt: 1 }}
                >
                  Add Facility
                </Button>
              </Box>
            )}
            {activeStep === 3 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>Review</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Please review the information below before submitting:
                </Typography>
                <Typography variant="h6">Basic Info</Typography>
                <Typography variant="body2">Building Name: {formData.buildingName}</Typography>
                <Typography variant="body2">Address: {formData.address}</Typography>
                <Typography variant="body2">Total Floors: {formData.totalFloors}</Typography>
                <Typography variant="body2">Total Units: {formData.totalUnits}</Typography>
                
                <Typography variant="h6" sx={{ mt: 2 }}>Payment Configuration</Typography>
                <Typography variant="body2">Razorpay Key ID: {formData.razorpayKeyId ? `${formData.razorpayKeyId.substring(0, 8)}...` : 'Not configured'}</Typography>
                <Typography variant="body2">Razorpay Key Secret: {formData.razorpayKeySecret ? '****** (Configured)' : 'Not configured'}</Typography>
                <Typography variant="h6" sx={{ mt: 2 }}>Blocks</Typography>
                {formData.blocks && formData.blocks.length > 0 ? (
                  formData.blocks.map((block, idx) => (
                    <Box key={idx} sx={{ mb: 1 }}>
                      <Typography variant="body2">Block Name: {block.blockName}</Typography>
                      <Typography variant="body2">Block ID: {block.blockId}</Typography>
                      <Typography variant="body2">Floors: {block.numFloors}</Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">No blocks added.</Typography>
                )}
                <Typography variant="h6" sx={{ mt: 2 }}>Facilities</Typography>
                {formData.facilities && formData.facilities.length > 0 ? (
                  formData.facilities.map((facility, idx) => (
                    <Box key={idx} sx={{ mb: 1 }}>
                      <Typography variant="body2">Name: {facility.name}</Typography>
                      <Typography variant="body2">Type: {facility.type}</Typography>
                      <Typography variant="body2">Status: {facility.status}</Typography>
                      <Typography variant="body2">Number of Slots: {facility.numberOfSlots || 1}</Typography>
                      <Typography variant="body2">Charge: {facility.chargeAmount} {facility.chargeUnit}</Typography>
                      <Typography variant="body2">Timings: {facility.timings && facility.timings.length > 0 ? facility.timings.join(', ') : '-'}</Typography>
                      <Typography variant="body2">Booking Required: {facility.bookingRequired ? 'Yes' : 'No'}</Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">No facilities added.</Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={buildingDialogActionsSx}>
            <Button 
              disabled={activeStep === 0} 
              onClick={() => setActiveStep(s => s - 1)}
              sx={{ minHeight: isMobile ? 44 : 'auto' }}
            >
              Back
            </Button>
            <Button 
              onClick={() => setActiveStep(s => s + 1)} 
              disabled={activeStep === steps.length - 1}
              sx={{ minHeight: isMobile ? 44 : 'auto' }}
            >
              Next
            </Button>
            {activeStep === steps.length - 1 && (
              <Button 
                type="submit" 
                variant="contained" 
                disabled={createMutation.isLoading}
                sx={{ minHeight: isMobile ? 44 : 'auto' }}
              >
                {createMutation.isLoading ? 'Adding...' : 'Add'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
      {/* Edit Building Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: buildingDialogPaperSx }}
      >
        <DialogTitle sx={buildingDialogTitleSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Edit color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Edit Building</Typography>
              <Typography variant="body2" color="text.secondary">
                Update building setup, blocks, facilities, and payment gateway details.
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <form onSubmit={handleEditSubmit}>
          <DialogContent sx={buildingDialogContentSx}>
            <Stepper
              activeStep={editActiveStep}
              orientation={isMobile ? "vertical" : "horizontal"}
              sx={{
                mb: 3,
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '& .MuiStepLabel-label': {
                  fontSize: isMobile ? '12px' : '14px'
                }
              }}
            >
              {editSteps.map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {editFormData && (
              <>
                {console.log('[Edit] Rendering form with editFormData:', editFormData)}
                {editActiveStep === 0 && (
                  <Box sx={{ display: 'grid', gap: 2 }}>
                    <BuildingFormSection title="Basic information" description="Name, address, and overall building capacity.">
                      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                        <TextField label="Building Name" value={editFormData.buildingName || ''} onChange={e => setEditFormData({ ...editFormData, buildingName: e.target.value })} required fullWidth />
                        <TextField label="Address" value={editFormData.address || ''} onChange={e => setEditFormData({ ...editFormData, address: e.target.value })} required fullWidth />
                        <TextField select label="Billing Mode" value={editFormData.billingMode || 'MAINTENANCE'} onChange={e => setEditFormData({ ...editFormData, billingMode: e.target.value })} required fullWidth>
                          <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                          <MenuItem value="RENTAL">Rental</MenuItem>
                        </TextField>
                        <TextField label="Total Floors" type="number" value={editFormData.totalFloors || ''} onChange={e => setEditFormData({ ...editFormData, totalFloors: e.target.value })} required fullWidth />
                        <TextField label="Total Units" type="number" value={editFormData.totalUnits || ''} onChange={e => setEditFormData({ ...editFormData, totalUnits: e.target.value })} required fullWidth />
                      </Box>
                    </BuildingFormSection>

                    <BuildingFormSection title="Payment gateway configuration" description="Optional credentials for online payment collection.">
                    <Tabs value={editPaymentTab} onChange={(e, newValue) => setEditPaymentTab(newValue)} aria-label="payment gateway tabs" sx={{ mb: 2 }}>
                      <Tab label="Razorpay" {...a11yProps(0)} />
                      <Tab label="CCAvenue" {...a11yProps(1)} />
                    </Tabs>
                    
                    <TabPanel value={editPaymentTab} index={0}>
                      {editFormData.hasRazorpayCredentials && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Current Razorpay Configuration:</strong>
                          </Typography>
                          <Typography variant="body2">
                            Razorpay Key ID: {editFormData.razorpayKeyId ? `${editFormData.razorpayKeyId}` : 'Configured'}
                          </Typography>
                          <Typography variant="body2">
                            Razorpay Key Secret: ••••••••••••••••
                          </Typography>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={editRazorpayCredentials}
                                onChange={(e) => {
                                  setEditRazorpayCredentials(e.target.checked);
                                  if (!e.target.checked) {
                                    // Clear the form fields when unchecked
                                    setEditFormData({
                                      ...editFormData,
                                      razorpayKeyId: editFormData.razorpayKeyId, // Keep original for display
                                      razorpayKeySecret: '' // Clear secret
                                    });
                                  }
                                }}
                              />
                            }
                            label="Edit Razorpay credentials"
                            sx={{ mt: 1 }}
                          />
                        </Box>
                      )}
                      
                      {(!editFormData.hasRazorpayCredentials || editRazorpayCredentials) && (
                        <>
                          <TextField 
                            label="Razorpay Key ID" 
                            value={editFormData.razorpayKeyId || ''} 
                            onChange={e => setEditFormData({ ...editFormData, razorpayKeyId: e.target.value })} 
                            placeholder="rzp_test_xxxxxxxxxx or rzp_live_xxxxxxxxxx"
                            helperText="Your Razorpay Key ID for payment processing"
                            fullWidth 
                            sx={{ mb: 2 }}
                          />
                          <TextField 
                            label="Razorpay Key Secret" 
                            type="password"
                            value={editFormData.razorpayKeySecret || ''} 
                            onChange={e => setEditFormData({ ...editFormData, razorpayKeySecret: e.target.value })} 
                            placeholder="Enter your Razorpay Key Secret"
                            helperText={editFormData.hasRazorpayCredentials ? "Leave blank to keep existing secret. Will be encrypted and stored securely." : "Will be encrypted and stored securely."}
                            fullWidth 
                          />
                        </>
                      )}
                    </TabPanel>
                    
                    <TabPanel value={editPaymentTab} index={1}>
                      {editFormData.hasCCavenueCredentials && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Current CCAvenue Configuration:</strong>
                          </Typography>
                          <Typography variant="body2">
                            Merchant ID: {editFormData.ccavenueMerchantId ? `${editFormData.ccavenueMerchantId}` : 'Configured'}
                          </Typography>
                          <Typography variant="body2">
                            Access Code: {editFormData.ccavenueAccessCode ? `${editFormData.ccavenueAccessCode}` : 'Configured'}
                          </Typography>
                          <Typography variant="body2">
                            Working Key: ••••••••••••••••
                          </Typography>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={editCCavenueCredentials}
                                onChange={(e) => {
                                  setEditCCavenueCredentials(e.target.checked);
                                  if (!e.target.checked) {
                                    // Clear the form fields when unchecked
                                    setEditFormData({
                                      ...editFormData,
                                      ccavenueMerchantId: editFormData.ccavenueMerchantId, // Keep original for display
                                      ccavenueAccessCode: editFormData.ccavenueAccessCode, // Keep original for display
                                      ccavenueWorkingKey: '', // Clear working key
                                      ccavenueSubAccountId: editFormData.ccavenueSubAccountId // Keep original for display
                                    });
                                  }
                                }}
                              />
                            }
                            label="Edit CCAvenue credentials"
                            sx={{ mt: 1 }}
                          />
                        </Box>
                      )}
                      
                      {(!editFormData.hasCCavenueCredentials || editCCavenueCredentials) && (
                        <>
                          <TextField 
                            label="CCAvenue Merchant ID" 
                            value={editFormData.ccavenueMerchantId || ''} 
                            onChange={e => setEditFormData({ ...editFormData, ccavenueMerchantId: e.target.value })} 
                            placeholder="Enter your CCAvenue Merchant ID"
                            helperText="Your CCAvenue Merchant ID for payment processing"
                            fullWidth 
                            sx={{ mb: 2 }}
                          />
                          <TextField 
                            label="CCAvenue Access Code" 
                            value={editFormData.ccavenueAccessCode || ''} 
                            onChange={e => setEditFormData({ ...editFormData, ccavenueAccessCode: e.target.value })} 
                            placeholder="Enter your CCAvenue Access Code"
                            helperText="Your CCAvenue Access Code for payment processing"
                            fullWidth 
                            sx={{ mb: 2 }}
                          />
                          <TextField 
                            label="CCAvenue Working Key" 
                            type="password"
                            value={editFormData.ccavenueWorkingKey || ''} 
                            onChange={e => setEditFormData({ ...editFormData, ccavenueWorkingKey: e.target.value })} 
                            placeholder="Enter your CCAvenue Working Key"
                            helperText={editFormData.hasCCavenueCredentials ? "Leave blank to keep existing working key. Will be encrypted and stored securely." : "Will be encrypted and stored securely."}
                            fullWidth 
                            sx={{ mb: 2 }}
                          />
                          <TextField 
                            label="CCAvenue Sub Account ID" 
                            value={editFormData.ccavenueSubAccountId || ''} 
                            onChange={e => setEditFormData({ ...editFormData, ccavenueSubAccountId: e.target.value })} 
                            placeholder="Enter your CCAvenue Sub Account ID (optional)"
                            helperText="Your CCAvenue Sub Account ID for marketplace transactions (optional)"
                            fullWidth 
                          />
                        </>
                      )}
                    </TabPanel>
                    </BuildingFormSection>
                  </Box>
                )}
                {editActiveStep === 1 && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Blocks / Towers</Typography>
                    {(editFormData.blocks || []).map((block, idx) => (
                      <Card key={block.blockId} sx={{ mb: 2 }}>
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                label="Block ID"
                                value={block.blockId}
                                InputProps={{ readOnly: true }}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                label="Block Name"
                                value={block.blockName}
                                onChange={e => handleEditBlockChange(idx, 'blockName', e.target.value)}
                                size="small"
                                fullWidth
                                required
                                error={!!error && error.includes('unique')}
                                helperText={!!error && error.includes('unique') ? error : ''}
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                label="Floors"
                                type="number"
                                value={block.numFloors}
                                onChange={e => handleEditBlockChange(idx, 'numFloors', e.target.value)}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                        <CardActions>
                          <Button size="small" color="error" onClick={() => handleEditRemoveBlock(idx)}>
                            Remove Block
                          </Button>
                        </CardActions>
                      </Card>
                    ))}
                    <Button
                      variant="outlined"
                      startIcon={<AddCircle />}
                      onClick={handleEditAddBlock}
                      sx={{ mt: 1 }}
                    >
                      Add Block
                    </Button>
                  </Box>
                )}
                {editActiveStep === 2 && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Facilities</Typography>
                    {(editFormData?.facilities || []).map((facility, idx) => (
                      <Card key={idx} sx={{ mb: 2 }}>
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                select
                                label="Type"
                                value={facility.type}
                                onChange={e => handleEditFacilityTypeChange(idx, e.target.value)}
                                size="small"
                                fullWidth
                              >
                                {facilityTypes.map(opt => (
                                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                label="Name"
                                value={facility.name}
                                onChange={e => handleEditFacilityChange(idx, 'name', e.target.value)}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                              <Typography variant="body2" sx={{ mb: 1 }}>Timings</Typography>
                              {(facility.timings || []).map((slot, slotIdx) => (
                                <Box key={slotIdx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <TextField
                                    value={slot}
                                    onChange={e => handleEditFacilityTimingsChange(idx, slotIdx, e.target.value)}
                                    size="small"
                                    placeholder="e.g. 06:00-10:00"
                                    sx={{ mr: 1, flex: 1 }}
                                  />
                                  <IconButton onClick={() => handleEditRemoveFacilityTiming(idx, slotIdx)} size="small">
                                    <RemoveCircle />
                                  </IconButton>
                                </Box>
                              ))}
                              <Button
                                size="small"
                                startIcon={<AddCircle />}
                                onClick={() => handleEditAddFacilityTiming(idx)}
                                sx={{ mt: 1 }}
                              >
                                Add Slot
                              </Button>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                select
                                label="Status"
                                value={facility.status}
                                onChange={e => handleEditFacilityChange(idx, 'status', e.target.value)}
                                size="small"
                                fullWidth
                              >
                                <MenuItem value="OPEN">Open</MenuItem>
                                <MenuItem value="CLOSED">Closed</MenuItem>
                                <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                              </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                label="Charge (₹)"
                                type="number"
                                value={facility.chargeAmount}
                                onChange={e => handleEditFacilityChange(idx, 'chargeAmount', e.target.value)}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                select
                                label="Charge Unit"
                                value={facility.chargeUnit}
                                onChange={e => handleEditFacilityChange(idx, 'chargeUnit', e.target.value)}
                                size="small"
                                fullWidth
                              >
                                {chargeUnits.map(opt => (
                                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                label="Description"
                                value={facility.description}
                                onChange={e => handleEditFacilityChange(idx, 'description', e.target.value)}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                select
                                label="Booking Required"
                                value={facility.bookingRequired ? 'true' : 'false'}
                                onChange={e => handleEditFacilityChange(idx, 'bookingRequired', e.target.value === 'true')}
                                size="small"
                                fullWidth
                              >
                                <MenuItem value="true">Yes</MenuItem>
                                <MenuItem value="false">No</MenuItem>
                              </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Number of Slots"
                                type="number"
                                value={facility.numberOfSlots || 1}
                                onChange={e => {
                                  const value = parseInt(e.target.value);
                                  if (value >= 1 && value <= 100) {
                                    handleEditFacilityChange(idx, 'numberOfSlots', value);
                                  } else if (e.target.value === '') {
                                    handleEditFacilityChange(idx, 'numberOfSlots', 1);
                                  }
                                }}
                                size="small"
                                fullWidth
                                inputProps={{ min: 1, max: 100, step: 1 }}
                                helperText="Enter number of available slots/units (1-100)"
                                placeholder="Enter number of slots"
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                        <CardActions>
                          <Button size="small" color="error" onClick={() => handleEditRemoveFacility(idx)}>
                            Remove Facility
                          </Button>
                        </CardActions>
                      </Card>
                    ))}
                    <Button
                      variant="outlined"
                      startIcon={<AddCircle />}
                      onClick={handleEditAddFacility}
                      sx={{ mt: 1 }}
                    >
                      Add Facility
                    </Button>
                  </Box>
                )}
                {editActiveStep === 3 && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Review</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Please review the information below before updating:
                    </Typography>
                    <Typography variant="h6">Basic Info</Typography>
                    <Typography variant="body2">Building Name: {editFormData.buildingName}</Typography>
                    <Typography variant="body2">Address: {editFormData.address}</Typography>
                    <Typography variant="body2">Total Floors: {editFormData.totalFloors}</Typography>
                    <Typography variant="body2">Total Units: {editFormData.totalUnits}</Typography>
                    
                    <Typography variant="h6" sx={{ mt: 2 }}>Payment Configuration</Typography>
                    <Typography variant="body2">Razorpay Key ID: {editFormData.razorpayKeyId ? `${editFormData.razorpayKeyId.substring(0, 8)}...` : 'Not configured'}</Typography>
                    <Typography variant="body2">Razorpay Key Secret: {editFormData.razorpayKeySecret ? '****** (Will be updated)' : 'No changes'}</Typography>
                    
                    <Typography variant="h6" sx={{ mt: 2 }}>Blocks</Typography>
                    {editFormData.blocks && editFormData.blocks.length > 0 ? (
                      editFormData.blocks.map((block, idx) => (
                        <Box key={idx} sx={{ mb: 1 }}>
                          <Typography variant="body2">Block Name: {block.blockName}</Typography>
                          <Typography variant="body2">Block ID: {block.blockId}</Typography>
                          <Typography variant="body2">Floors: {block.numFloors}</Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">No blocks added.</Typography>
                    )}
                    
                    <Typography variant="h6" sx={{ mt: 2 }}>Facilities</Typography>
                    {editFormData.facilities && editFormData.facilities.length > 0 ? (
                      editFormData.facilities.map((facility, idx) => (
                        <Box key={idx} sx={{ mb: 1 }}>
                          <Typography variant="body2">Name: {facility.name}</Typography>
                          <Typography variant="body2">Type: {facility.type}</Typography>
                          <Typography variant="body2">Status: {facility.status}</Typography>
                          <Typography variant="body2">Number of Slots: {facility.numberOfSlots || 1}</Typography>
                          <Typography variant="body2">Charge: {facility.chargeAmount} {facility.chargeUnit}</Typography>
                          <Typography variant="body2">Timings: {facility.timings && facility.timings.length > 0 ? facility.timings.join(', ') : '-'}</Typography>
                          <Typography variant="body2">Booking Required: {facility.bookingRequired ? 'Yes' : 'No'}</Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">No facilities added.</Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={buildingDialogActionsSx}>
            <Button onClick={handleEditClose} sx={{ minHeight: isMobile ? 44 : 'auto' }}>
              Cancel
            </Button>
            <Button 
              disabled={editActiveStep === 0} 
              onClick={() => setEditActiveStep(s => s - 1)}
              sx={{ minHeight: isMobile ? 44 : 'auto' }}
            >
              Back
            </Button>
            <Button 
              onClick={() => setEditActiveStep(s => s + 1)} 
              disabled={editActiveStep === editSteps.length - 1 || !canProceedToNextEditStep()}
              sx={{ minHeight: isMobile ? 44 : 'auto' }}
            >
              Next
            </Button>
            {editActiveStep === editSteps.length - 1 && (
              <Button 
                type="submit" 
                variant="contained" 
                disabled={updateMutation.isLoading}
                sx={{ minHeight: isMobile ? 44 : 'auto' }}
              >
                {updateMutation.isLoading ? 'Updating...' : 'Update'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Buildings;
